import styled, { css } from 'styled-components';
import { deviceLess } from '_constants/DEVICES';
import { UiColors } from 'assets/styles/old/global';

/**
 * Shared layout atoms for the new-UI list pages (Retailers, Users, Venues, Youth, ...). These are
 * dumb, presentational styled-components meant to be composed in JSX per page — a page includes the
 * sidebar only if it has view tabs, etc. Behavior lives in hooks/useListController; the table
 * chrome lives in modules/ListTable.
 */
export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 20px;

  @media (max-width: 1023px) {
    gap: 12px;
    padding: 12px;
  }
  /* Contain the page's z-indexes: the full-screen map overlay (MapShell, z-index 120) outranked
     the nav rail (60), top bar (70) and sub-pages popup (80), leaving the navigation unusable with
     its menus opening behind the map. The overlay is already inset by topOffset/leftOffset so the
     chrome stays visible; isolation creates a stacking context without becoming a containing
     block, so the fixed overlay's geometry is unchanged. */
  isolation: isolate;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

export const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${UiColors.forestColor};
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

/**
 * Top-of-content filter bar: the Filter trigger (with applied-count badge) sits immediately left of
 * the chips it produces. Wrapped as a white rounded card (matching the DataTable card) so the row
 * reads as its own panel.
 */
export const FilterBar = styled.div`
  display: flex;
  /* Top-align so the Filter button + chips keep a fixed position; the horizontal scrollbar that
     appears with many chips grows into the space below instead of shifting everything up. */
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  min-height: 60px;
  box-sizing: border-box;
  /* The gap to the table below. Lived only on the Retailers list' private copy of this component,
     so every other page sat tighter to its table. */
  margin-bottom: 16px;
  padding: 8px 7px 2px;
  background: ${UiColors.fillWhiteColor};
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 10px;
`;

export const FilterTrigger = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: ${UiColors.forestColor};
  color: ${UiColors.peachColor};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    filter: saturate(1.3);
  }
`;

export const FilterCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: ${UiColors.orangeColor};
  color: ${UiColors.fillWhiteColor};
  font-size: 12px;
  font-weight: 700;
`;

/**
 * Two-column shell for pages that have a view sidebar (e.g. Venues' List/Map). Flex rather than a
 * `220px 1fr` grid so the sidebar is genuinely optional: a page that hides it for some roles (Campaign
 * list, for users without change_campaign) gets a full-width content area instead of having the table
 * auto-placed into the empty 220px sidebar track. The sidebar width is a custom property so
 * <SidebarColumn /> can size itself from the `collapsed` prop set here.
 */
export const Layout = styled.div`
  --sidebar-width: ${({ collapsed }) => (collapsed ? '56px' : '220px')};
  display: flex;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 1023px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

export const SidebarColumn = styled.aside`
  flex: 0 0 var(--sidebar-width);
  min-width: 0;
  transition: flex-basis 0.18s ease;
  position: sticky;
  top: 16px;
  background: ${UiColors.fillWhiteColor};
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 8px;
  padding: 8px 0 12px;

  @media (max-width: 1023px) {
    flex: none;
  }
`;

export const SidebarHeader = styled.div`
  display: flex;
  justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'flex-end')};
  padding: ${({ collapsed }) => (collapsed ? '0 6px 8px' : '0 12px 8px')};
  margin-bottom: 4px;
  border-bottom: 1px solid ${UiColors.strokeColor};
`;

export const CollapseToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 6px;
  background: ${UiColors.fillWhiteColor};
  color: ${UiColors.forestColor};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${UiColors.cloudColor};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ContentArea = styled.div`
  flex: 1 1 0;
  min-width: 0;

  @media (max-width: 1023px) {
    flex: none;
  }
`;

export const SidebarDivider = styled.div`
  height: 1px;
  background: ${UiColors.strokeColor};
  margin: 8px 12px;
`;

/**
 * Width-constrained wrapper for the legacy <Filter> (search input + Search button) so it fits in the
 * DataTable toolbar row. On mobile it drops the Search button and rounds the input into a compact,
 * self-contained field that shares the first toolbar row (Enter / clear still run the search).
 */
/**
 * The new design's skin for the legacy <Filter /> search box (ui-kit/old/filter): a 34px field with
 * an 8px radius joined to a forest "Search" button, overriding the legacy chrome by class name.
 *
 * A mixin rather than part of <SearchWrap /> alone, because pages that are shared between the two
 * designs (Driving Directions) need the same skin behind their own new-UI flag check, with their own
 * layout around it.
 */
export const newUiSearchField = css`
  .form_search {
    width: 100%;
    height: 34px;
    box-sizing: border-box;
    border: 1px solid ${UiColors.strokeColor} !important;
    border-right: none !important;
    border-radius: 8px 0 0 8px;
    background: ${UiColors.fillWhiteColor} !important;
  }
  .form_search input {
    background: transparent !important;
  }
  .form_search:hover,
  .form_search:hover > input {
    background-color: ${UiColors.fillWhiteColor} !important;
    border-color: ${UiColors.strokeColor} !important;
    cursor: text;
  }

  .searchButton {
    height: 34px;
    border-radius: 0;
    background-color: ${UiColors.forestColor};
    color: ${UiColors.peachColor};
  }

  /* Only the control that ends the group rounds the right edge; on toolbars with an
     advanced-search caret after the button, the caret closes it instead. */
  .searchButton:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  @media (max-width: 1023px) {
    .searchButton {
      display: none;
    }
    .form_search {
      border-right: 1px solid ${UiColors.strokeColor} !important;
      border-radius: 8px !important;
    }
  }
`;

/**
 * Placeholder overlay for the search field, mirroring the campaign view's marquee.
 *
 * The field's own placeholder is left empty (pass placeholder="" to <Filter/>) so the two never
 * double up, and this clipped copy scrolls its tail into view while the field is focused and empty.
 * Without it a long placeholder -- "Search by retailer name, address, city and/or zip code" -- is
 * simply cut off with no way to read the rest.
 *
 * Render it as a sibling of <Filter/> inside <SearchWrap/>, and only while the value is empty.
 */
export const PlaceholderMarquee = styled.div`
  position: absolute;
  top: 1px;
  left: 1px;
  height: 32px;
  box-sizing: border-box;
  /* The text area only: clear the loupe (8 + 14 + 8px in inputSearch), the clear cross (~34px) and
     the Search button (70px), so the text never paints over any of them. */
  width: calc(100% - 70px - 34px - 2px);
  display: flex;
  align-items: center;
  padding-left: 30px;
  overflow: hidden;
  pointer-events: none;

  @media (max-width: 1023px) {
    /* newUiSearchField hides the Search button below 1024px; the clear cross stays. */
    width: calc(100% - 34px - 2px);
  }

  span {
    white-space: nowrap;
    font-family: Roboto;
    font-size: 14px;
    color: ${UiColors.fontColor};
    /* Transform rather than text-indent: GPU-composited, so the scroll stays smooth. */
    will-change: transform;
  }
`;

export const SearchWrap = styled.div`
  /* Containing block for PlaceholderMarquee above. */
  position: relative;
  display: flex;
  align-items: center;
  flex: 0 1 360px;
  min-width: 240px;

  & > div:first-child,
  form {
    width: 100%;
  }
  form {
    margin-right: 0;
  }

  &:focus-within ${PlaceholderMarquee} span {
    animation: scaffold-search-marquee 6s ease-in-out infinite alternate;
  }

  @keyframes scaffold-search-marquee {
    0%,
    12% {
      transform: translateX(0);
    }
    88%,
    100% {
      transform: translateX(-170px);
    }
  }

  ${newUiSearchField}

  @media (max-width: 1023px) {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    max-width: none;
  }
`;

/* Groups the toolbar's left controls (search + any action buttons) on one line. */
export const LeftCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  flex-wrap: wrap;

  & > button {
    height: 34px;
  }
`;

/* Rounds the shared ExportPopover's print + Export trigger buttons to match the new design. */
export const ExportWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  & > div > button {
    border-radius: 8px;
  }
`;

export const Count = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${UiColors.forestColor};
  white-space: nowrap;
`;

/**
 * Wraps a map slot so it can toggle to a full-screen overlay (used by list pages with a Map view).
 * Normally a relative box (so the floating toggle anchors to it); when `fullscreen` it becomes a
 * fixed overlay filling the content region — offset by the top bar + nav rail so those stay visible
 * — and stretches the legacy map (otherwise height-locked) to fill it.
 */
export const MapShell = styled.div`
  position: relative;

  ${({ fullscreen, topOffset, leftOffset }) =>
    fullscreen &&
    css`
      position: fixed;
      top: ${topOffset}px;
      left: ${leftOffset}px;
      right: 0;
      bottom: 0;
      z-index: 120;
      background: ${UiColors.fillWhiteColor};

      > div {
        height: 100%;
      }
      > div > div {
        height: 100% !important;
        max-height: none !important;
      }
    `}
`;

/* Floating full-screen toggle, offset left of the map's native top-right controls (basemap/zoom). */
export const MapFullscreenToggle = styled.button`
  position: absolute;
  top: 10px;
  right: 64px;
  z-index: 130;
  box-sizing: border-box;
  width: 30px;
  height: 30px;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${UiColors.strokeColor};
  border-radius: 8px;
  background: ${UiColors.fillWhiteColor};
  color: ${UiColors.forestColor};
  cursor: pointer;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: background 0.15s ease, right 120ms ease;

  &:hover {
    background: ${UiColors.cloudColor};
  }

  svg {
    width: 16px;
    height: 16px;
  }

  /* Travels with the map's other right-hand controls when the retailer popup docks over that edge
     (flag + width set on <body> by MapPopup), so it doesn't end up hidden underneath it. */
  @media ${deviceLess.tableView} {
    body.map-popup-docked & {
      right: calc(64px + var(--map-popup-docked-width, 0px));
    }
  }
`;
